import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCalendarAlt, FaMapMarkerAlt } from 'react-icons/fa';

const ExperienceSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #00d4ff;
`;

const Timeline = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #00d4ff, #0099cc);
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const ExperienceItem = styled(motion.div)`
  position: relative;
  margin-bottom: 3rem;
  display: flex;
  align-items: center;
  
  &:nth-child(odd) {
    flex-direction: row;
    
    @media (max-width: 768px) {
      flex-direction: row;
    }
  }
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    @media (max-width: 768px) {
      flex-direction: row;
    }
  }
  
  @media (max-width: 768px) {
    margin-left: 40px;
  }
`;

const TimelineNode = styled.div`
  position: absolute;
  left: 50%;
  width: 20px;
  height: 20px;
  background: #00d4ff;
  border: 4px solid #0f0f23;
  border-radius: 50%;
  transform: translateX(-50%);
  z-index: 2;
  
  @media (max-width: 768px) {
    left: -30px;
    transform: translateX(0);
  }
`;

const ExperienceCard = styled(motion.div)`
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  width: 45%;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    width: 0;
    height: 0;
    border: 10px solid transparent;
    transform: translateY(-50%);
  }
  
  .left &::before {
    right: -20px;
    border-left-color: rgba(0, 212, 255, 0.2);
  }
  
  .right &::before {
    left: -20px;
    border-right-color: rgba(0, 212, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    
    &::before {
      display: none;
    }
  }
`;

const CompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CompanyIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
`;

const CompanyInfo = styled.div`
  flex: 1;
`;

const CompanyName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.2rem;
`;

const Position = styled.h4`
  font-size: 1.1rem;
  color: #00d4ff;
  font-weight: 500;
`;

const DateLocation = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #b8b8b8;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Description = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.8rem;
    color: #b8b8b8;
    line-height: 1.6;
    
    &::before {
      content: '▶';
      position: absolute;
      left: 0;
      color: #00d4ff;
      font-size: 0.8rem;
    }
  }
`;

const Experience = () => {
  const experiences = [
    {
      company: "Cyperts Digital Solution",
      position: "Blockchain Developer",
      period: "Jan 2023 - Jan 2025",
      location: "Remote",
      description: [
        "Developed and deployed Ethereum-based smart contracts using Solidity, ensuring efficient and secure transaction processing",
        "Led the development of decentralized applications (DApps) using Web3.js, Ethers.js, and React, integrating smart contracts with front-end interfaces",
        "Created ERC-20, BEP-20, ERC-1155, and ERC-721 tokens for various clients, facilitating tokenomics and digital asset management",
        "Integrated DeFi protocols into platforms, adding features like staking and liquidity pools",
        "Developed unit tests for smart contracts using Truffle and Hardhat, significantly reducing bugs and vulnerabilities before deployment"
      ]
    },
    {
      company: "Donatuz",
      position: "Blockchain Developer",
      period: "Jan 2025 - March 2025",
      location: "Remote",
      description: [
        "Worked with Thirdweb SDK to integrate on-chain functionalities such as token swaps, liquidity pools, and staking, enhancing DApp performance and blockchain interaction",
        "Integrated Uniswap smart contracts into decentralized applications, enabling seamless token swaps, liquidity provisioning, and automated market-making",
        "Developed and deployed custom liquidity pools and swap mechanisms on Uniswap V2, optimizing trading efficiency and reducing slippage"
      ]
    },
    {
      company: "QTech",
      position: "Blockchain Developer",
      period: "April 2025",
      location: "Remote",
      description: [
        "Developed a Rust-based Layer-1 blockchain node, including core modules for networking, consensus, transaction validation, and state management",
        "Implemented P2P networking using Rust, enabling efficient peer discovery, block propagation, and transaction gossip across distributed nodes",
        "Built and integrated a Kademlia DHT for decentralized peer routing, improving network scalability, resilience, and fast node lookup",
        "Designed and developed a high-performance RPC node to interact with the blockchain, supporting methods for transaction broadcasting, block queries, account state retrieval, and network diagnostics",
        "Worked on validator node development, including block proposal logic, signature verification, and participation in consensus rounds"
      ]
    }
  ];

  return (
    <ExperienceSection id="experience">
      <Title
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        Professional Experience
      </Title>

      <Timeline>
        {experiences.map((exp, index) => (
          <ExperienceItem
            key={index}
            initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: index * 0.2 }}
            viewport={{ once: true }}
            className={index % 2 === 0 ? 'left' : 'right'}
          >
            <TimelineNode />
            <ExperienceCard
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <CompanyHeader>
                <CompanyIcon>
                  {exp.company.split(' ').map(word => word[0]).join('').substring(0, 2)}
                </CompanyIcon>
                <CompanyInfo>
                  <CompanyName>{exp.company}</CompanyName>
                  <Position>{exp.position}</Position>
                </CompanyInfo>
              </CompanyHeader>
              
              <DateLocation>
                <DateInfo>
                  <FaCalendarAlt />
                  <span>{exp.period}</span>
                </DateInfo>
                <DateInfo>
                  <FaMapMarkerAlt />
                  <span>{exp.location}</span>
                </DateInfo>
              </DateLocation>
              
              <Description>
                {exp.description.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </Description>
            </ExperienceCard>
          </ExperienceItem>
        ))}
      </Timeline>
    </ExperienceSection>
  );
};

export default Experience;